import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { isEqual } from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { LoggerService } from "@/shared/services/LoggerService";
import { TopicData, useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import {
  Entity,
  VectorField,
  VectorFieldDisplayMode,
} from "@/shared/state/visualization/schema/v1/three-d";

import {
  FileSelector,
  PathSelector,
} from "../../../controls/DataSourceSelector";
import { isViewableAsPlot } from "../../../Panel/predicates";
import {
  useDataFiles,
  useFileIdToTopics,
  useWorkspaceFiles,
} from "../../../WorkspaceCtx/hooks";
import { ConfigItem } from "../ConfigItem";

import styles from "./EntityConfig.module.css";
import {
  joinedPathFor,
  topicDataFor,
} from "./PositionConfig/positionTopicData";
import {
  type AxisName,
  reconcileAxisPathsToFile,
} from "./PositionConfig/reconcileAxisPathsToFile";
import { resolveAxesFileSelection } from "./PositionConfig/resolveAxesFileSelection";
import {
  buildVectorFieldSpec,
  parseNumberInput,
  validateStride,
} from "./vectorFieldValidation";

const DEFAULT_COLOR = "#e85d3a";
const DEFAULT_GRADIENT_LOW = "#0000ff";
const DEFAULT_GRADIENT_HIGH = "#ff0000";

interface Props {
  entity: Entity;
  panelId: string;
}

export function VectorFieldConfig({ entity, panelId }: Props) {
  const dispatch = useVizDispatch();
  const availableFiles = useDataFiles();
  const fileIdToTopics = useFileIdToTopics();

  // The unfiltered workspace list: resolveAxesFileSelection reports the file
  // the axes are bound to as dangling when it is absent here, and
  // `availableFiles` drops any file whose topics have not arrived yet, so
  // passing that list would show the "not in the current workspace" error over
  // a file that is present. The FileSelector dropdown still offers
  // `availableFiles`, since a file with no topics is not a valid pick.
  const workspaceFiles = useWorkspaceFiles();

  // Resolve the file shown in the FileSelector. Bound axes derive the file
  // implicitly. When no axes are bound, the user's last FileSelector pick is
  // held in `scopingFile` so the topic tree shown to PathSelectors stays in
  // scope until they bind a path.
  const fileSelection = resolveAxesFileSelection(
    entity.vectorField,
    workspaceFiles,
  );
  const [scopingFile, setScopingFile] = React.useState<string | undefined>(
    undefined,
  );
  const effectiveFileId =
    fileSelection.fileId ??
    scopingFile ??
    (availableFiles.length === 1 && !fileSelection.dangling
      ? availableFiles[0].file_id
      : undefined);
  const topicTree = effectiveFileId
    ? fileIdToTopics[effectiveFileId] ?? []
    : [];

  // The vectorField this component last dispatched or last loaded into the form.
  const vectorFieldRef = React.useRef(entity.vectorField);

  // Path selectors seed their text box once, when they mount. Bumping this key
  // remounts all three so they show the current topic tree and paths.
  const reloadKeyRef = React.useRef(0);

  const [forwardData, setForwardData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.vectorField?.forward),
  );
  const [rightData, setRightData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.vectorField?.right),
  );
  const [upData, setUpData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.vectorField?.up),
  );

  const [forwardScale, setForwardScale] = React.useState(
    `${entity.vectorField?.forwardScale ?? 1}`,
  );
  const [forwardScaleError, setForwardScaleError] = React.useState("");
  const [rightScale, setRightScale] = React.useState(
    `${entity.vectorField?.rightScale ?? 1}`,
  );
  const [rightScaleError, setRightScaleError] = React.useState("");
  const [upScale, setUpScale] = React.useState(
    `${entity.vectorField?.upScale ?? 1}`,
  );
  const [upScaleError, setUpScaleError] = React.useState("");

  const [lineWidth, setLineWidth] = React.useState(
    `${entity.vectorField?.lineWidth ?? 2}`,
  );
  const [lineWidthError, setLineWidthError] = React.useState("");
  const [color, setColor] = React.useState(
    entity.vectorField?.color || DEFAULT_COLOR,
  );
  const [stride, setStride] = React.useState(
    `${entity.vectorField?.stride ?? 1}`,
  );
  const [strideError, setStrideError] = React.useState("");
  const [displayMode, setDisplayMode] = React.useState<VectorFieldDisplayMode>(
    entity.vectorField?.displayMode ?? "current_only",
  );
  const [gradientEnabled, setGradientEnabled] = React.useState(
    entity.vectorField?.gradientEnabled ?? false,
  );
  const [colorLow, setColorLow] = React.useState(
    entity.vectorField?.colorLow || DEFAULT_GRADIENT_LOW,
  );
  const [colorHigh, setColorHigh] = React.useState(
    entity.vectorField?.colorHigh || DEFAULT_GRADIENT_HIGH,
  );

  const forwardPath = joinedPathFor(entity.vectorField?.forward);
  const rightPath = joinedPathFor(entity.vectorField?.right);
  const upPath = joinedPathFor(entity.vectorField?.up);

  // The user picks a different file: axes whose path also resolves in that
  // file's topic tree are rebound to it, the rest reset to 0, and the whole
  // reconciled vector field goes out in one dispatch. A vector field records no
  // file of its own; its file is whichever one its axis bindings name.
  const onFileChange = React.useCallback(
    (newFileId: string) => {
      setScopingFile(newFileId);

      const newFileTopicTree = fileIdToTopics[newFileId] ?? [];
      const preserved = reconcileAxisPathsToFile(
        entity.vectorField,
        newFileTopicTree,
      );

      const reconciled: VectorField = {
        ...(entity.vectorField ?? {
          color: DEFAULT_COLOR,
          lineWidth: 2,
        }),
        forward: 0,
        forwardScale: entity.vectorField?.forwardScale ?? 1,
        right: 0,
        rightScale: entity.vectorField?.rightScale ?? 1,
        up: 0,
        upScale: entity.vectorField?.upScale ?? 1,
      };
      const preservedData: Partial<Record<AxisName, TopicData>> = {};
      for (const { axis, node } of preserved) {
        try {
          const topicData = node.toTopicData();
          reconciled[axis] = topicData;
          preservedData[axis] = topicData;
        } catch (err) {
          // toTopicData throws when the topic offers no representation to
          // visualize. Swallow it and leave this axis at 0 so the others still
          // rebind; the warning names the axis the user will see cleared.
          LoggerService.warn(
            `VectorField: could not rebind ${axis} axis on file change`,
            { path: node.data.message_path, err },
          );
        }
      }

      // Match the form state to what is being dispatched; otherwise the effect
      // below rebuilds the vector field out of the previous file's TopicData
      // and dispatches that over this one.
      setForwardData(preservedData.forward);
      setRightData(preservedData.right);
      setUpData(preservedData.up);

      vectorFieldRef.current = reconciled;
      reloadKeyRef.current++;

      dispatch(
        updateThreeDEntity(panelId, entity.id, { vectorField: reconciled }),
      );
    },
    [entity.vectorField, entity.id, panelId, dispatch, fileIdToTopics],
  );

  React.useEffect(
    function onFieldsChanged() {
      if (!isEqual(vectorFieldRef.current, entity.vectorField)) {
        // Differs from what this component last dispatched, so it was set from
        // outside: reload every field from it and skip the rebuild below.
        vectorFieldRef.current = entity.vectorField;
        setForwardScale(`${entity.vectorField?.forwardScale ?? 1}`);
        setRightScale(`${entity.vectorField?.rightScale ?? 1}`);
        setUpScale(`${entity.vectorField?.upScale ?? 1}`);
        setLineWidth(`${entity.vectorField?.lineWidth ?? 2}`);
        setColor(entity.vectorField?.color || DEFAULT_COLOR);
        setStride(`${entity.vectorField?.stride ?? 1}`);
        setDisplayMode(entity.vectorField?.displayMode ?? "current_only");
        setGradientEnabled(entity.vectorField?.gradientEnabled ?? false);
        setColorLow(entity.vectorField?.colorLow || DEFAULT_GRADIENT_LOW);
        setColorHigh(entity.vectorField?.colorHigh || DEFAULT_GRADIENT_HIGH);

        setForwardData(topicDataFor(entity.vectorField?.forward));
        setRightData(topicDataFor(entity.vectorField?.right));
        setUpData(topicDataFor(entity.vectorField?.up));
        reloadKeyRef.current++;
        return;
      }

      const parsedForwardScale = parseNumberInput(
        forwardScale,
        "Forward scale",
      );
      const parsedRightScale = parseNumberInput(rightScale, "Right scale");
      const parsedUpScale = parseNumberInput(upScale, "Up scale");
      const parsedLineWidth = parseNumberInput(lineWidth, "Line width");
      const parsedStride = parseNumberInput(stride, "Stride");

      setForwardScaleError(parsedForwardScale.error ?? "");
      setRightScaleError(parsedRightScale.error ?? "");
      setUpScaleError(parsedUpScale.error ?? "");
      setLineWidthError(parsedLineWidth.error ?? "");
      setStrideError(parsedStride.error ?? "");

      if (
        !parsedForwardScale.ok ||
        !parsedRightScale.ok ||
        !parsedUpScale.ok ||
        !parsedLineWidth.ok ||
        !parsedStride.ok
      ) {
        return;
      }

      const strideValidationError = validateStride(parsedStride.value);
      if (strideValidationError) {
        setStrideError(strideValidationError);
        return;
      }

      const updated = buildVectorFieldSpec(
        {
          forwardData,
          rightData,
          upData,
          forwardScale: parsedForwardScale.value,
          rightScale: parsedRightScale.value,
          upScale: parsedUpScale.value,
          lineWidth: parsedLineWidth.value,
          stride: parsedStride.value,
          color,
          displayMode,
          gradientEnabled,
          colorLow,
          colorHigh,
        },
        entity.vectorField,
      );

      if (updated && !isEqual(updated, entity.vectorField)) {
        vectorFieldRef.current = updated;
        dispatch(
          updateThreeDEntity(panelId, entity.id, { vectorField: updated }),
        );
      }
    },
    [
      forwardData,
      rightData,
      upData,
      forwardScale,
      rightScale,
      upScale,
      lineWidth,
      color,
      stride,
      displayMode,
      gradientEnabled,
      colorLow,
      colorHigh,
      entity.vectorField,
      entity.id,
      panelId,
      dispatch,
    ],
  );

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        Vector Field
      </RobotoTypography>
      <div className={styles.container}>
        {(availableFiles.length >= 2 || fileSelection.dangling) && (
          <ConfigItem label="File">
            <FileSelector
              className={styles.blockSpacing}
              files={availableFiles}
              selection={effectiveFileId}
              onSelect={onFileChange}
              isError={fileSelection.dangling}
              forceShow={fileSelection.dangling}
              helperText={
                fileSelection.dangling
                  ? "The file this vector field was bound to is not in the current workspace. Pick a file to continue."
                  : undefined
              }
            />
          </ConfigItem>
        )}
        <div key={reloadKeyRef.current} className={styles.axisGroup}>
          <ConfigItem label="Right Axis">
            <PathSelector
              className={styles.blockSpacing}
              initialInputValue={rightPath}
              onChange={(node) => setRightData(node.toTopicData())}
              options={topicTree}
              optionFilter={isViewableAsPlot}
            />
          </ConfigItem>
          <ConfigItem label="Forward Axis">
            <PathSelector
              className={styles.blockSpacing}
              initialInputValue={forwardPath}
              onChange={(node) => setForwardData(node.toTopicData())}
              options={topicTree}
              optionFilter={isViewableAsPlot}
            />
          </ConfigItem>
          <ConfigItem label="Up Axis">
            <PathSelector
              className={styles.blockSpacing}
              initialInputValue={upPath}
              onChange={(node) => setUpData(node.toTopicData())}
              options={topicTree}
              optionFilter={isViewableAsPlot}
            />
          </ConfigItem>
        </div>
        <ConfigItem label="Scaling">
          <div className={styles.fieldRow}>
            <TextField
              label="Right Scale"
              type="number"
              size="small"
              margin="dense"
              value={rightScale}
              error={!!rightScaleError}
              helperText={rightScaleError}
              onChange={(e) => setRightScale(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ step: 0.1, className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Forward Scale"
              type="number"
              size="small"
              margin="dense"
              value={forwardScale}
              error={!!forwardScaleError}
              helperText={forwardScaleError}
              onChange={(e) => setForwardScale(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ step: 0.1, className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Up Scale"
              type="number"
              size="small"
              margin="dense"
              value={upScale}
              error={!!upScaleError}
              helperText={upScaleError}
              onChange={(e) => setUpScale(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ step: 0.1, className: styles.input }}
              className={styles.scaleField}
            />
          </div>
        </ConfigItem>
        <ConfigItem label="Display">
          <FormControl
            size="small"
            fullWidth
            className={`${styles.field} ${styles.displayControl}`}
          >
            <InputLabel
              id={`vf-display-mode-label-${entity.id}`}
              className={styles.input}
            >
              Display Mode
            </InputLabel>
            <Select
              label="Display Mode"
              labelId={`vf-display-mode-label-${entity.id}`}
              value={displayMode}
              onChange={(e) =>
                setDisplayMode(e.target.value as VectorFieldDisplayMode)
              }
              className={styles.input}
              inputProps={{ className: styles.input }}
            >
              <MenuItem value="current_only">
                Display current vector only
              </MenuItem>
              <MenuItem value="field_only">
                Display all vectors over time
              </MenuItem>
            </Select>
          </FormControl>
          <div className={styles.fieldRow}>
            <TextField
              label="Line Width"
              type="number"
              size="small"
              margin="dense"
              value={lineWidth}
              error={!!lineWidthError}
              helperText={lineWidthError}
              onChange={(e) => setLineWidth(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 0, step: 1, className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Color"
              type="color"
              size="small"
              margin="dense"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Stride"
              type="number"
              size="small"
              margin="dense"
              value={stride}
              error={!!strideError}
              helperText={strideError}
              onChange={(e) => setStride(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 1, step: 1, className: styles.input }}
              className={styles.scaleField}
            />
          </div>
        </ConfigItem>
        <ConfigItem label="Gradient">
          <div className={styles.fieldRow}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={gradientEnabled}
                  onChange={(e) => setGradientEnabled(e.target.checked)}
                  size="small"
                />
              }
              label="Enable gradient"
              className={styles.input}
            />
          </div>
          {gradientEnabled && (
            <div className={styles.fieldRow}>
              <TextField
                label="Low Color"
                type="color"
                size="small"
                margin="dense"
                value={colorLow}
                onChange={(e) => setColorLow(e.target.value)}
                InputLabelProps={{ className: styles.input }}
                InputProps={{ className: styles.input }}
                inputProps={{ className: styles.input }}
                className={styles.scaleField}
              />
              <TextField
                label="High Color"
                type="color"
                size="small"
                margin="dense"
                value={colorHigh}
                onChange={(e) => setColorHigh(e.target.value)}
                InputLabelProps={{ className: styles.input }}
                InputProps={{ className: styles.input }}
                inputProps={{ className: styles.input }}
                className={styles.scaleField}
              />
            </div>
          )}
        </ConfigItem>
      </div>
    </>
  );
}
